/**
 * Grid Selection Modal
 * Allows admins to view and select pricing grids/plans for an organization.
 * Shows detailed plan info, pricing, features, and conflict warnings.
 * Supports enterprise "inquire" plans with custom pricing.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { 
  AlertTriangle, Check, ChevronRight, Crown, 
  Users, Package, Sparkles, Loader2, Calendar,
  TrendingUp, TrendingDown, ArrowRight, Building2,
  DollarSign, CalendarDays, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../services/api';

const GridSelectionModal = ({ 
  isOpen, 
  onClose, 
  organization, 
  onPlanChanged,
  colors 
}) => {
  const [step, setStep] = useState('select'); // 'select', 'confirm', 'resolve', 'comp_confirm'
  const [loading, setLoading] = useState(false);
  const [grids, setGrids] = useState([]);
  const [selectedGrid, setSelectedGrid] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [validation, setValidation] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  
  // Resolution selections
  const [demoteUserIds, setDemoteUserIds] = useState([]);
  const [archiveProductIds, setArchiveProductIds] = useState([]);
  const [grantGracePeriod, setGrantGracePeriod] = useState(false);
  
  // Comp account confirmation (when org has no Stripe subscription)
  const [compConfirmation, setCompConfirmation] = useState(null);
  
  // Enterprise plan fields
  const [customSeatPriceCents, setCustomSeatPriceCents] = useState('');
  const [contractType, setContractType] = useState('annual');
  const [contractStart, setContractStart] = useState('');
  const [contractEnd, setContractEnd] = useState('');
  const [enterpriseDowngradeConfirmed, setEnterpriseDowngradeConfirmed] = useState(false);

  // Fetch available grids and org info when modal opens
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !organization) return;
      
      setLoading(true);
      try {
        const [gridsRes, orgRes] = await Promise.all([
          api.get('/api/admin/org-plans/grids?include_archived=true'),
          api.get(`/api/admin/org-plans/organization/${organization.organization_id}`)
        ]);
        setGrids(gridsRes.data.grids || []);
        setOrgInfo(orgRes.data);
        
        // Pre-select current grid and plan
        if (orgRes.data.organization?.grid_version_id) {
          setSelectedGrid(orgRes.data.organization.grid_version_id);
        }
        if (orgRes.data.organization?.plan_id) {
          setSelectedPlan(orgRes.data.organization.plan_id);
        }
      } catch (err) {
        console.error('Failed to fetch grid data:', err);
        toast.error('Failed to load pricing grids');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isOpen, organization]);

  const validateChange = async () => {
    if (!selectedGrid || !selectedPlan) {
      toast.error('Please select a grid and plan');
      return;
    }

    // Skip validation if selecting current plan
    if (selectedGrid === orgInfo?.organization?.grid_version_id && 
        selectedPlan === orgInfo?.organization?.plan_id) {
      toast.info('Organization is already on this plan');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/admin/org-plans/validate-change', {
        organization_id: organization.organization_id,
        new_grid_version_id: selectedGrid,
        new_plan_id: selectedPlan
      });
      
      setValidation(res.data);
      
      // If selecting an enterprise (inquire) plan, go to enterprise config step
      if (res.data.new_is_inquire) {
        // Pre-populate if there's existing enterprise deal data
        if (res.data.current_enterprise_deal) {
          setCustomSeatPriceCents(
            res.data.current_enterprise_deal.custom_seat_price_cents 
              ? String(res.data.current_enterprise_deal.custom_seat_price_cents / 100) 
              : ''
          );
          setContractType(res.data.current_enterprise_deal.contract_type || 'annual');
          setContractStart(res.data.current_enterprise_deal.contract_start || '');
          setContractEnd(res.data.current_enterprise_deal.contract_end || '');
        } else {
          // Default to annual starting today
          const today = new Date().toISOString().split('T')[0];
          const oneYearFromNow = new Date();
          oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
          setContractStart(today);
          setContractEnd(oneYearFromNow.toISOString().split('T')[0]);
        }
        setStep('enterprise');
      } else if (res.data.conflicts && res.data.conflicts.length > 0) {
        setStep('resolve');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      console.error('Validation failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to validate plan change');
    } finally {
      setLoading(false);
    }
  };

  const applyChange = async (confirmCompAccount = false) => {
    setLoading(true);
    try {
      const payload = {
        organization_id: organization.organization_id,
        new_grid_version_id: selectedGrid,
        new_plan_id: selectedPlan,
        demote_user_ids: demoteUserIds,
        archive_product_ids: archiveProductIds,
        grant_grace_period: grantGracePeriod,
        confirm_comp_account: confirmCompAccount
      };
      
      // Include enterprise deal fields if this is an inquire plan
      if (validation?.new_is_inquire) {
        payload.custom_seat_price_cents = Math.round(parseFloat(customSeatPriceCents) * 100);
        payload.contract_type = contractType;
        payload.contract_start = contractStart;
        payload.contract_end = contractEnd;
      }
      
      const res = await api.post('/api/admin/org-plans/apply-change', payload);
      
      // Check if confirmation is required for comp account
      if (res.data?.requires_confirmation && res.data?.confirmation_type === 'comp_account') {
        setCompConfirmation(res.data);
        setStep('comp_confirm');
        setLoading(false);
        return;
      }
      
      // Show appropriate message based on result
      if (grantGracePeriod) {
        toast.success('Grace period granted. Organization has 15 days to resolve.');
      } else if (res.data?.subscription_status === 'active_admin') {
        toast.success('Plan assigned as complimentary account (no billing).');
      } else {
        const stripeAction = res.data?.stripe_result?.action;
        if (stripeAction === 'plan_updated') {
          toast.success('Plan changed and Stripe subscription updated!');
        } else if (stripeAction === 'scheduled_cancellation') {
          toast.success('Plan changed. Subscription will be cancelled at period end.');
        } else if (stripeAction === 'needs_checkout') {
          toast.success('Plan changed. Organization will need to complete checkout for billing.');
        } else if (validation?.new_is_inquire) {
          toast.success('Enterprise plan configured successfully!');
        } else {
          toast.success('Plan changed successfully!');
        }
      }
      
      onPlanChanged?.();
      handleClose();  // Use handleClose to reset all state
    } catch (err) {
      console.error('Apply change failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to apply plan change');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle comp account confirmation
  const confirmCompAccountCreation = () => {
    applyChange(true);
  };

  const handleClose = () => {
    setStep('select');
    setSelectedGrid(null);
    setSelectedPlan(null);
    setValidation(null);
    setDemoteUserIds([]);
    setArchiveProductIds([]);
    setGrantGracePeriod(false);
    setCompConfirmation(null);
    setCustomSeatPriceCents('');
    setContractType('annual');
    setContractStart('');
    setContractEnd('');
    setEnterpriseDowngradeConfirmed(false);
    onClose();
  };
  
  // Render comp account confirmation step
  const renderCompConfirmation = () => (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-800 dark:text-amber-200">Complimentary Account</h3>
            <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
              <strong>{compConfirmation?.organization_name}</strong> does not have an active billing subscription.
            </p>
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
              Proceeding will grant them complimentary access to the selected plan. The account will be marked as 
              <strong> administrator-provisioned</strong> and will not be charged.
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">What this means:</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Full access to the selected plan's features</li>
          <li>• Token allocation as defined by the plan</li>
          <li>• No billing or payment collection</li>
          <li>• Account marked as "Comped" in admin view</li>
          <li>• Customer sees "Complimentary Access" instead of billing details</li>
        </ul>
      </div>
    </div>
  );

  const formatPrice = (cents) => {
    if (!cents) return 'Free';
    return `$${(cents / 100).toFixed(0)}/mo`;
  };

  const getGridPlans = (gridVersionId) => {
    const grid = grids.find(g => g.version_id === gridVersionId);
    return grid?.plans || [];
  };

  // Render grid selection step
  const renderGridSelection = () => (
    <div className="space-y-4">
      {/* Current status */}
      {orgInfo && (
        <Card className={cn("p-4 border", colors?.bgSecondary, colors?.borderPrimary)}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className={cn("font-medium", colors?.textPrimary)}>Current Assignment</h4>
              <p className={cn("text-sm", colors?.textMuted)}>
                {orgInfo.current_grid?.name || 'No grid assigned'} 
                {orgInfo.current_plan && ` → ${orgInfo.current_plan.label}`}
              </p>
            </div>
            <div className="text-right">
              <p className={cn("text-sm", colors?.textMuted)}>Usage</p>
              <p className={cn("text-sm font-medium", colors?.textPrimary)}>
                {orgInfo.usage?.paid_seats || 0} seats · {orgInfo.usage?.products || 0} products
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Grid list */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {grids.map(grid => (
          <Card 
            key={grid.version_id}
            className={cn(
              "p-4 border-2 cursor-pointer transition-all",
              selectedGrid === grid.version_id ? "border-blue-500 bg-blue-50/50" : colors?.borderPrimary,
              colors?.bgCard
            )}
            onClick={() => {
              setSelectedGrid(grid.version_id);
              // Auto-select first plan if not already selected
              if (grid.plans?.length > 0 && !grid.plans.find(p => p.plan_id === selectedPlan)) {
                setSelectedPlan(grid.plans[0].plan_id);
              }
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className={cn("font-semibold", colors?.textPrimary)}>{grid.name}</h4>
                  {grid.is_live && (
                    <Badge className="bg-green-100 text-green-700">Live</Badge>
                  )}
                  {orgInfo?.organization?.grid_version_id === grid.version_id && (
                    <Badge variant="outline">Current</Badge>
                  )}
                  {orgInfo?.organization?.signup_grid_version_id === grid.version_id && (
                    <Badge variant="secondary">Signup Grid</Badge>
                  )}
                </div>
                <p className={cn("text-xs mt-1", colors?.textMuted)}>
                  Variant {grid.variant} · Deployed {new Date(grid.deployed_at).toLocaleDateString()}
                  {grid.org_count > 0 && ` · ${grid.org_count} org${grid.org_count > 1 ? 's' : ''}`}
                </p>
              </div>
              {selectedGrid === grid.version_id && (
                <Check className="w-5 h-5 text-blue-500" />
              )}
            </div>

            {/* Plans in this grid */}
            {selectedGrid === grid.version_id && (
              <div className="mt-4 pt-4 border-t space-y-2">
                <p className={cn("text-sm font-medium", colors?.textMuted)}>Select Plan:</p>
                <div className="grid grid-cols-2 gap-2">
                  {grid.plans?.map(plan => (
                    <div
                      key={plan.plan_id}
                      className={cn(
                        "p-3 rounded-lg border-2 cursor-pointer transition-all",
                        selectedPlan === plan.plan_id 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-gray-200 hover:border-gray-300"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlan(plan.plan_id);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className={cn("font-medium", colors?.textPrimary)}>{plan.label}</span>
                        <span className={cn("text-sm font-bold", colors?.textPrimary)}>
                          {formatPrice(plan.price_cents)}
                        </span>
                      </div>
                      <p className={cn("text-xs mt-1", colors?.textMuted)}>
                        {plan.plan_type === 'free' ? 'No credit card' : 'Per paid seat'}
                      </p>
                      {plan.restrictions && (
                        <div className={cn("text-xs mt-2 space-y-1", colors?.textMuted)}>
                          {plan.restrictions.max_paid_seats && (
                            <p>Max {plan.restrictions.max_paid_seats} paid seats</p>
                          )}
                          {plan.restrictions.max_products && (
                            <p>Max {plan.restrictions.max_products} products</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  // Render conflict resolution step
  const renderConflictResolution = () => {
    const seatsConflict = validation?.conflicts?.find(c => c.type === 'seats');
    const productsConflict = validation?.conflicts?.find(c => c.type === 'products');
    const featuresConflict = validation?.conflicts?.find(c => c.type === 'features');

    return (
      <div className="space-y-4">
        <div className={cn("p-4 rounded-lg bg-amber-50 border border-amber-200")}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Conflicts Detected</h4>
              <p className="text-sm text-amber-700 mt-1">
                The organization exceeds limits for the new plan. Resolve conflicts or grant a 15-day grace period.
              </p>
            </div>
          </div>
        </div>

        {/* Seats conflict */}
        {seatsConflict && (
          <Card className={cn("p-4 border", colors?.borderPrimary)}>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-red-500" />
              <h4 className={cn("font-medium", colors?.textPrimary)}>
                Paid Seats: {seatsConflict.current_count} → {seatsConflict.new_limit}
              </h4>
              <Badge variant="destructive">-{seatsConflict.excess}</Badge>
            </div>
            <p className={cn("text-sm mb-3", colors?.textMuted)}>
              Select {seatsConflict.excess} user(s) to demote to Collaborator:
            </p>
            <div className="space-y-2 max-h-[150px] overflow-y-auto">
              {seatsConflict.items?.map(user => (
                <label
                  key={user.user_id}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded border cursor-pointer hover:bg-gray-50",
                    demoteUserIds.includes(user.user_id) && "bg-red-50 border-red-200"
                  )}
                >
                  <Checkbox
                    checked={demoteUserIds.includes(user.user_id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setDemoteUserIds([...demoteUserIds, user.user_id]);
                      } else {
                        setDemoteUserIds(demoteUserIds.filter(id => id !== user.user_id));
                      }
                    }}
                    disabled={user.role === 'owner'}
                  />
                  <div className="flex-1">
                    <p className={cn("font-medium", colors?.textPrimary)}>{user.name || user.email}</p>
                    <p className={cn("text-xs", colors?.textMuted)}>
                      {user.role} {user.role === 'owner' && '(cannot demote)'}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            <p className={cn("text-xs mt-2", colors?.textMuted)}>
              Selected: {demoteUserIds.length}/{seatsConflict.excess} required
            </p>
          </Card>
        )}

        {/* Products conflict */}
        {productsConflict && (
          <Card className={cn("p-4 border", colors?.borderPrimary)}>
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-red-500" />
              <h4 className={cn("font-medium", colors?.textPrimary)}>
                Products: {productsConflict.current_count} → {productsConflict.new_limit}
              </h4>
              <Badge variant="destructive">-{productsConflict.excess}</Badge>
            </div>
            <p className={cn("text-sm mb-3", colors?.textMuted)}>
              Select {productsConflict.excess} product(s) to archive:
            </p>
            <div className="space-y-2 max-h-[150px] overflow-y-auto">
              {productsConflict.items?.map(product => (
                <label
                  key={product.product_id}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded border cursor-pointer hover:bg-gray-50",
                    archiveProductIds.includes(product.product_id) && "bg-red-50 border-red-200"
                  )}
                >
                  <Checkbox
                    checked={archiveProductIds.includes(product.product_id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setArchiveProductIds([...archiveProductIds, product.product_id]);
                      } else {
                        setArchiveProductIds(archiveProductIds.filter(id => id !== product.product_id));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <p className={cn("font-medium", colors?.textPrimary)}>{product.name}</p>
                    <p className={cn("text-xs", colors?.textMuted)}>
                      Created {new Date(product.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            <p className={cn("text-xs mt-2", colors?.textMuted)}>
              Selected: {archiveProductIds.length}/{productsConflict.excess} required
            </p>
          </Card>
        )}

        {/* Features conflict (no selection, just warning) */}
        {featuresConflict && (
          <Card className={cn("p-4 border border-red-200 bg-red-50")}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-red-500" />
              <h4 className="font-medium text-red-800">
                Features: {featuresConflict.current_count} → {featuresConflict.new_limit}
              </h4>
            </div>
            <p className="text-sm text-red-700 mt-2">
              Archive products or delete features to meet the limit, or grant a grace period.
            </p>
          </Card>
        )}

        {/* Grace period option */}
        <Card className={cn("p-4 border", colors?.borderPrimary)}>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={grantGracePeriod}
              onCheckedChange={setGrantGracePeriod}
            />
            <div>
              <p className={cn("font-medium", colors?.textPrimary)}>
                Grant 15-day grace period
              </p>
              <p className={cn("text-sm", colors?.textMuted)}>
                Allow the organization to resolve conflicts themselves. After 15 days, excess items will be automatically archived and users demoted.
              </p>
            </div>
          </label>
        </Card>
      </div>
    );
  };

  // Render enterprise configuration step
  const renderEnterpriseConfig = () => {
    const newPlan = getGridPlans(selectedGrid).find(p => p.plan_id === selectedPlan);
    
    return (
      <div className="space-y-4">
        {/* Header info */}
        <div className={cn("p-4 rounded-lg bg-purple-50 border border-purple-200")}>
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-purple-800">Enterprise Plan Configuration</h4>
              <p className="text-sm text-purple-700 mt-1">
                Set custom pricing for this organization&apos;s enterprise agreement.
              </p>
            </div>
          </div>
        </div>

        {/* Custom pricing input */}
        <Card className={cn("p-4 border", colors?.borderPrimary)}>
          <Label className={cn("block mb-2 font-medium", colors?.textPrimary)}>
            <DollarSign className="w-4 h-4 inline mr-1" />
            Custom Monthly Price per Seat
          </Label>
          <div className="flex items-center gap-2">
            <span className={cn("text-lg", colors?.textPrimary)}>$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={customSeatPriceCents}
              onChange={(e) => setCustomSeatPriceCents(e.target.value)}
              placeholder="e.g., 49.99"
              className="w-40"
            />
            <span className={cn("text-sm", colors?.textMuted)}>/seat/month</span>
          </div>
          <p className={cn("text-xs mt-2", colors?.textMuted)}>
            This is the negotiated per-seat price for this enterprise contract.
          </p>
        </Card>

        {/* Contract type */}
        <Card className={cn("p-4 border", colors?.borderPrimary)}>
          <Label className={cn("block mb-2 font-medium", colors?.textPrimary)}>
            <CalendarDays className="w-4 h-4 inline mr-1" />
            Contract Type
          </Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="contractType"
                value="annual"
                checked={contractType === 'annual'}
                onChange={(e) => setContractType(e.target.value)}
                className="w-4 h-4 text-purple-600"
              />
              <span className={cn(colors?.textPrimary)}>Annual</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="contractType"
                value="monthly"
                checked={contractType === 'monthly'}
                onChange={(e) => setContractType(e.target.value)}
                className="w-4 h-4 text-purple-600"
              />
              <span className={cn(colors?.textPrimary)}>Monthly</span>
            </label>
          </div>
        </Card>

        {/* Contract dates */}
        <Card className={cn("p-4 border", colors?.borderPrimary)}>
          <Label className={cn("block mb-2 font-medium", colors?.textPrimary)}>
            Contract Period
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={cn("text-sm", colors?.textMuted)}>Start Date</Label>
              <Input
                type="date"
                value={contractStart}
                onChange={(e) => setContractStart(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className={cn("text-sm", colors?.textMuted)}>End Date</Label>
              <Input
                type="date"
                value={contractEnd}
                onChange={(e) => setContractEnd(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </Card>

        {/* Summary */}
        {customSeatPriceCents && (
          <Card className={cn("p-4 border bg-gray-50", colors?.borderPrimary)}>
            <h4 className={cn("font-medium mb-2", colors?.textPrimary)}>Summary</h4>
            <div className={cn("text-sm space-y-1", colors?.textMuted)}>
              <p>Plan: <span className="font-medium text-purple-600">{newPlan?.label}</span></p>
              <p>Price: <span className="font-medium">${parseFloat(customSeatPriceCents).toFixed(2)}/seat/month</span></p>
              <p>Contract: <span className="font-medium capitalize">{contractType}</span></p>
              {contractStart && contractEnd && (
                <p>Period: <span className="font-medium">
                  {new Date(contractStart).toLocaleDateString()} - {new Date(contractEnd).toLocaleDateString()}
                </span></p>
              )}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Render confirmation step
  const renderConfirmation = () => {
    const newPlan = getGridPlans(selectedGrid).find(p => p.plan_id === selectedPlan);
    const newGrid = grids.find(g => g.version_id === selectedGrid);

    return (
      <div className="space-y-4">
        <Card className={cn("p-4 border", colors?.bgSecondary, colors?.borderPrimary)}>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className={cn("text-sm", colors?.textMuted)}>Current</p>
              <p className={cn("font-medium", colors?.textPrimary)}>
                {orgInfo?.current_grid?.name || 'None'} → {orgInfo?.current_plan?.label || 'None'}
              </p>
            </div>
            <ArrowRight className={cn("w-5 h-5", colors?.textMuted)} />
            <div className="flex-1 text-right">
              <p className={cn("text-sm", colors?.textMuted)}>New</p>
              <p className={cn("font-medium text-blue-600")}>
                {newGrid?.name} → {newPlan?.label}
              </p>
            </div>
          </div>
        </Card>

        {validation?.change_type && (
          <div className={cn(
            "p-4 rounded-lg border",
            validation.change_type === 'upgrade' 
              ? "bg-green-50 border-green-200" 
              : validation.change_type === 'downgrade'
              ? "bg-amber-50 border-amber-200"
              : "bg-blue-50 border-blue-200"
          )}>
            <div className="flex items-center gap-2">
              {validation.change_type === 'upgrade' ? (
                <TrendingUp className="w-5 h-5 text-green-600" />
              ) : validation.change_type === 'downgrade' ? (
                <TrendingDown className="w-5 h-5 text-amber-600" />
              ) : (
                <ArrowRight className="w-5 h-5 text-blue-600" />
              )}
              <span className={cn(
                "font-medium",
                validation.change_type === 'upgrade' ? "text-green-700" :
                validation.change_type === 'downgrade' ? "text-amber-700" : "text-blue-700"
              )}>
                {validation.change_type.charAt(0).toUpperCase() + validation.change_type.slice(1)}
              </span>
            </div>
            {validation.billing_impact && (
              <p className={cn(
                "text-sm mt-1",
                validation.change_type === 'upgrade' ? "text-green-600" :
                validation.change_type === 'downgrade' ? "text-amber-600" : "text-blue-600"
              )}>
                Price: {formatPrice(validation.billing_impact.current_price_cents)} → {formatPrice(validation.billing_impact.new_price_cents)}
              </p>
            )}
          </div>
        )}

        {/* Enterprise downgrade warning */}
        {validation?.will_lose_enterprise_deal && (
          <Card className="p-4 border-2 border-red-300 bg-red-50">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-red-800">⚠️ Enterprise Deal Will Be Lost</h4>
                <p className="text-sm text-red-700 mt-1">
                  This organization has a custom enterprise agreement that will be permanently deleted if you proceed:
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1 ml-4 list-disc">
                  <li>
                    Custom Price: <span className="font-semibold">
                      ${(validation.current_enterprise_deal?.custom_seat_price_cents / 100).toFixed(2)}/seat/month
                    </span>
                  </li>
                  {validation.current_enterprise_deal?.contract_type && (
                    <li>
                      Contract Type: <span className="font-semibold capitalize">
                        {validation.current_enterprise_deal.contract_type}
                      </span>
                    </li>
                  )}
                  {validation.current_enterprise_deal?.contract_start && (
                    <li>
                      Contract Period: <span className="font-semibold">
                        {new Date(validation.current_enterprise_deal.contract_start).toLocaleDateString()} - {new Date(validation.current_enterprise_deal.contract_end).toLocaleDateString()}
                      </span>
                    </li>
                  )}
                </ul>
                <div className="mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={enterpriseDowngradeConfirmed}
                      onCheckedChange={setEnterpriseDowngradeConfirmed}
                    />
                    <span className="text-sm font-medium text-red-800">
                      I understand this will permanently delete the enterprise agreement
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Enterprise plan summary in confirmation */}
        {validation?.new_is_inquire && customSeatPriceCents && (
          <Card className={cn("p-4 border bg-purple-50 border-purple-200")}>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-purple-600" />
              <h4 className="font-medium text-purple-800">Enterprise Agreement</h4>
            </div>
            <div className="text-sm space-y-1 text-purple-700">
              <p>Price: <span className="font-medium">${parseFloat(customSeatPriceCents).toFixed(2)}/seat/month</span></p>
              <p>Contract: <span className="font-medium capitalize">{contractType}</span></p>
              {contractStart && contractEnd && (
                <p>Period: <span className="font-medium">
                  {new Date(contractStart).toLocaleDateString()} - {new Date(contractEnd).toLocaleDateString()}
                </span></p>
              )}
            </div>
          </Card>
        )}

        <p className={cn("text-sm", colors?.textMuted)}>
          This will update the organization&apos;s plan immediately. Stripe billing will be adjusted accordingly.
        </p>
      </div>
    );
  };

  // Check if resolution is complete
  const isResolutionComplete = () => {
    if (grantGracePeriod) return true;
    
    const seatsConflict = validation?.conflicts?.find(c => c.type === 'seats');
    const productsConflict = validation?.conflicts?.find(c => c.type === 'products');
    const featuresConflict = validation?.conflicts?.find(c => c.type === 'features');

    if (seatsConflict && demoteUserIds.length < seatsConflict.excess) return false;
    if (productsConflict && archiveProductIds.length < productsConflict.excess) return false;
    if (featuresConflict && !grantGracePeriod) return false;

    return true;
  };

  // Check if enterprise config is complete
  const isEnterpriseConfigComplete = () => {
    if (!customSeatPriceCents || parseFloat(customSeatPriceCents) <= 0) return false;
    if (!contractType) return false;
    if (!contractStart || !contractEnd) return false;
    return true;
  };

  // Check if confirmation can proceed (handle enterprise downgrade)
  const canConfirm = () => {
    if (validation?.will_lose_enterprise_deal && !enterpriseDowngradeConfirmed) {
      return false;
    }
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {step === 'select' && 'Select Pricing Grid & Plan'}
            {step === 'enterprise' && 'Configure Enterprise Plan'}
            {step === 'resolve' && 'Resolve Conflicts'}
            {step === 'confirm' && 'Confirm Plan Change'}
            {step === 'comp_confirm' && 'Confirm Complimentary Access'}
          </DialogTitle>
          <DialogDescription>
            {organization?.name || 'Organization'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          )}

          {!loading && step === 'select' && renderGridSelection()}
          {!loading && step === 'enterprise' && renderEnterpriseConfig()}
          {!loading && step === 'resolve' && renderConflictResolution()}
          {!loading && step === 'confirm' && renderConfirmation()}
          {!loading && step === 'comp_confirm' && renderCompConfirmation()}
        </div>

        <DialogFooter>
          {step === 'select' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={validateChange} 
                disabled={!selectedGrid || !selectedPlan || loading}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Review Change
              </Button>
            </>
          )}
          
          {step === 'comp_confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('confirm')}>Back</Button>
              <Button 
                onClick={confirmCompAccountCreation}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Grant Complimentary Access
              </Button>
            </>
          )}

          {step === 'enterprise' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')}>Back</Button>
              <Button 
                onClick={() => {
                  // After enterprise config, check for conflicts
                  if (validation?.conflicts && validation.conflicts.length > 0) {
                    setStep('resolve');
                  } else {
                    setStep('confirm');
                  }
                }}
                disabled={!isEnterpriseConfigComplete() || loading}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Continue
              </Button>
            </>
          )}

          {step === 'resolve' && (
            <>
              <Button variant="outline" onClick={() => {
                // Go back to enterprise step if it's an inquire plan
                if (validation?.new_is_inquire) {
                  setStep('enterprise');
                } else {
                  setStep('select');
                }
              }}>Back</Button>
              <Button 
                onClick={() => applyChange()}
                disabled={!isResolutionComplete() || loading}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {grantGracePeriod ? 'Grant Grace Period' : 'Apply Changes'}
              </Button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => {
                // Go back to enterprise step if it's an inquire plan, otherwise select
                if (validation?.new_is_inquire) {
                  setStep('enterprise');
                } else {
                  setStep('select');
                }
              }}>Back</Button>
              <Button 
                onClick={() => applyChange()} 
                disabled={loading || !canConfirm()}
                className={validation?.will_lose_enterprise_deal ? "bg-red-600 hover:bg-red-700" : ""}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {validation?.will_lose_enterprise_deal 
                  ? 'Confirm & Remove Enterprise Deal' 
                  : 'Confirm Change'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GridSelectionModal;
